/**
 * Field-unit resolution — the glue between the field-units registry, the
 * resolved profiles and the raw conversion math. Pure functions (no React, no
 * I/O) so the whole pipeline is unit-testable.
 *
 * The pipeline for a rendered measurement (see the UOM architecture):
 *   1. field → quantity class          (field-units registry)
 *   2. stored unit  = the field's class resolved in the *record's* profile
 *                     (or a per-field `unit_override` which pins a unit)
 *   3. display unit = the field's class resolved in the *viewer's* profile
 *   4. convert stored→display via ABCD, label with the display unit's symbol
 */
import type {
  ConvertedValue,
  FieldUnitDescriptor,
  FieldUnitsMap,
  QuantityClass,
  ResolvedProfile,
  ResolvedUnit,
} from "./types";
import { convert } from "./convert";

/** Classes that never profile-convert (money is FX'd separately; the rest pass through). */
const NON_PROFILE: ReadonlySet<QuantityClass> = new Set<QuantityClass>([
  "currency",
  "duration",
  "count",
]);

/** Look up the descriptor for a table.column in the field-units registry. */
export function fieldDescriptor(
  map: FieldUnitsMap | undefined,
  table: string,
  col: string,
): FieldUnitDescriptor | undefined {
  return map?.fields?.[table]?.[col];
}

/**
 * Resolve a unit for a quantity class in a profile, honouring a per-field
 * `unit_override` (which pins a fixed unit across every dimension in the profile
 * — e.g. hole size is always inches). Returns undefined if unresolvable.
 */
export function resolveUnit(
  profile: ResolvedProfile | undefined,
  quantityClass: QuantityClass,
  override?: string,
): ResolvedUnit | undefined {
  if (!profile) return undefined;
  if (override) {
    // The override names a concrete unit key; find it anywhere in the profile.
    const hit = Object.values(profile.units).find((u) => u.unit_key === override);
    if (hit) return hit;
  }
  return profile.units[quantityClass];
}

/**
 * Convert a single field value from its stored profile to the display profile.
 *
 * `storedProfile` / `displayProfile` are the fully-resolved profiles. When the
 * field's class is non-dimensional (currency/duration/count) or the units can't
 * be resolved, the value passes through unchanged with an empty label.
 */
export function convertField(
  map: FieldUnitsMap | undefined,
  table: string,
  col: string,
  value: number | null | undefined,
  storedProfile: ResolvedProfile | undefined,
  displayProfile: ResolvedProfile | undefined,
): ConvertedValue {
  const desc = fieldDescriptor(map, table, col);
  const quantityClass = desc?.quantity_class ?? "count";

  // Non-dimensional or unmapped — never touch the number, no unit label.
  if (!desc || NON_PROFILE.has(quantityClass)) {
    return { value: value ?? null, unit: "", quantityClass };
  }

  const override = desc.unit_override;
  // A pinned unit (e.g. inches) means stored AND display use that same fixed
  // unit — no conversion, the value is already in it.
  if (override) {
    const u = resolveUnit(storedProfile ?? displayProfile, quantityClass, override);
    return { value: value ?? null, unit: u?.symbol ?? override, quantityClass };
  }

  const from = resolveUnit(storedProfile, quantityClass);
  const to = resolveUnit(displayProfile, quantityClass);

  // Missing either side — show the raw value in whichever unit we do know.
  if (!from || !to) {
    const known = to ?? from;
    return { value: value ?? null, unit: known?.symbol ?? "", quantityClass };
  }

  return { value: convert(value, from, to), unit: to.symbol, quantityClass };
}
